<script setup lang="ts">
import { ref, computed, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

import {
  TranslationFieldsType,
  TranslationType,
} from "@/common/types/translation";

import {
  baseInput,
  baseSelect,
  baseTextarea,
  baseTextEditor,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update:data", "salve:data"]);

const props = defineProps<{
  fields: TranslationFieldsType[];
  data: TranslationType;
  language: string;
}>();

const language = ref("");
const payload = ref<{ [key: string]: string }>({});

const languagesOptions = computed(() => [
  {
    option: "Português (BR)",
    value: "pt-br",
  },
  {
    option: "English (US)",
    value: "en-us",
  },
]);

function update() {
  emit("update:data", {
    language: language.value,
    metadata: JSON.stringify(payload.value),
  });
}

function resumeData() {
  if (!props.fields) return;

  payload.value = props.fields?.reduce((acc, item) => {
    acc[item.field] = item.value;
    return acc;
  }, {});

  if (props.data) {
    language.value = props.data.language;

    if (props.data?.metadata) {
      try {
        const metadata = props.data.metadata;

        if (metadata) {
          props.fields?.forEach((item) => {
            if (metadata[item.field]) {
              payload.value[item.field] = metadata[item.field];
            }
          });
        }
      } catch (_) {}
    }
  }
}

watchEffect(() => {
  resumeData();
});
</script>

<template>
  <article class="form-category">
    <base-select
      v-model="language"
      :value="language"
      :options="languagesOptions"
      :is-empty="false"
      @change="update()"
    >
      <template #label>
        {{ t("common.labels.type") }}
      </template>
    </base-select>

    <template v-for="item in props.fields" :key="item.field">
      <template v-if="item.type === 'input'">
        <base-input
          v-model="payload[item.field]"
          :value="payload[item.field]"
          @blur="update()"
        >
          <template #label>
            {{ t(item.label) }}
          </template>
        </base-input>
      </template>

      <template v-if="item.type === 'textarea'">
        <base-text-editor v-model="payload[item.field]" @blur="update()">
          <template #label>
            {{ t(item.label) }}
          </template>
        </base-text-editor>
      </template>
    </template>
  </article>
</template>

<style lang="scss" scoped>
.form-category {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}
</style>
